import api from './api';
import { AdminSettings, EmailReplyTemplate, GoldReplyExample, TourDefinition, SystemAuditLogEntry } from '../types';

export const adminService = {
  /**
   * Get system settings from backend
   */
  getSettings: async (): Promise<AdminSettings> => {
    try {
      const response = await api.get('/admin/settings');
      const loaded = response.data?.data || response.data;
      const { INITIAL_SETTINGS } = await import('../constants');
      return {
        ...INITIAL_SETTINGS,
        ...loaded,
        sleepOverPolicy: {
          ...INITIAL_SETTINGS.sleepOverPolicy,
          ...(loaded?.sleepOverPolicy || {}),
        },
        routeDefaults: {
          ...INITIAL_SETTINGS.routeDefaults,
          ...(loaded?.routeDefaults || {}),
        },
        cancellationPolicies: {
          ...INITIAL_SETTINGS.cancellationPolicies,
          ...(loaded?.cancellationPolicies || {}),
          charter: {
            ...INITIAL_SETTINGS.cancellationPolicies.charter,
            ...(loaded?.cancellationPolicies?.charter || {}),
          },
          scheduled: {
            ...INITIAL_SETTINGS.cancellationPolicies.scheduled,
            ...(loaded?.cancellationPolicies?.scheduled || {}),
          },
        },
        discountCapsByRole: loaded?.discountCapsByRole || INITIAL_SETTINGS.discountCapsByRole,
        permissions: loaded?.permissions || INITIAL_SETTINGS.permissions,
        outbound: {
          ...INITIAL_SETTINGS.outbound,
          ...(loaded?.outbound || {}),
          email: {
            ...(INITIAL_SETTINGS.outbound?.email || { accounts: [] }),
            ...(loaded?.outbound?.email || {}),
            defaultAccountByUsage:
              loaded?.outbound?.email?.defaultAccountByUsage ||
              INITIAL_SETTINGS.outbound?.email?.defaultAccountByUsage ||
              {},
          },
        },
        sPhone: {
          ...(INITIAL_SETTINGS.sPhone || {
            enabled: false,
            wsServer: '',
            domain: '',
            autoRegister: true,
            defaultOutboundPrefix: '',
            iceServers: ['stun:stun.l.google.com:19302'],
            accounts: [],
          }),
          ...(loaded?.sPhone || {}),
          accounts: Array.isArray(loaded?.sPhone?.accounts) ? loaded.sPhone.accounts : (INITIAL_SETTINGS.sPhone?.accounts || []),
        },
        whatsappTransfer: {
          ...(INITIAL_SETTINGS.whatsappTransfer || {
            enabled: true,
            popupEnabled: true,
            autoIntroEnabled: true,
            allowOperatorOverride: true,
            autoIntroTemplate: "Hi, I'm taking over your request now. I will assist you from here.",
            returnToOwnerTemplate: "Welcome back {{clientName}}, I am now back on your chat and will continue assisting you.",
            askFeedbackBeforeReturn: false,
            feedbackPromptTemplate: "Before I return you to my colleague, was this support helpful? (Yes/No)",
          }),
          ...(loaded?.whatsappTransfer || {}),
        },
        whatsappRecall: {
          ...(INITIAL_SETTINGS.whatsappRecall || {
            enabled: true,
            maxRecallMinutes: 120,
            allowLeadershipOverride: true,
            requireReason: false,
            allowMediaRecall: true,
          }),
          ...(loaded?.whatsappRecall || {}),
        },
        emailSystem: {
          ...(INITIAL_SETTINGS.emailSystem || {}),
          ...(loaded?.emailSystem || {}),
          inboundBridge: {
            ...(INITIAL_SETTINGS.emailSystem?.inboundBridge || {
              enabled: false,
              ingestSecret: '',
              mailboxAddress: 'fly@miracleair.tz',
              allowEnvFallback: true,
            }),
            ...(loaded?.emailSystem?.inboundBridge || {}),
            automation: {
              ...(
                INITIAL_SETTINGS.emailSystem?.inboundBridge?.automation || {
                  enabled: true,
                  autoBackfillOnRecovery: true,
                  staleAfterMinutes: 120,
                  deadAfterMinutes: 360,
                  autoBackfillCooldownMinutes: 30,
                }
              ),
              ...(loaded?.emailSystem?.inboundBridge?.automation || {}),
            },
          },
          outboundBridge: {
            ...(INITIAL_SETTINGS.emailSystem?.outboundBridge || {
              enabled: false,
              provider: 'H.BRD_GMAIL',
              webhookUrl: '',
              authHeaderName: 'x-bridge-secret',
              authHeaderValue: '',
              timeoutMs: 15000,
              allowSmtpFallback: true,
            }),
            ...(loaded?.emailSystem?.outboundBridge || {}),
          },
          intake: {
            ...(INITIAL_SETTINGS.emailSystem?.intake || {
              enabled: true,
              defaultInboxKey: 'fly',
              hideNonFlightByDefault: true,
              hideSpamByDefault: true,
              blockedDomains: [],
              allowlistDomains: [],
              allowlistEmails: [],
              spamSubjectPatterns: [],
              alerts: {
                enabled: true,
                businessHoursOnly: false,
                firstResponseSlaMinutes: 15,
                escalationSteps: [],
                autoResolveOnFirstOutbound: true,
              },
              automation: {
                enabled: true,
                replyOnFirstInboundOnly: true,
                applyToCategories: ['CHARTER', 'SCHEDULED', 'UNCERTAIN'],
                mailboxKeys: [],
                cooldownMinutes: 60,
                askMissingFields: true,
                subjectTemplate: 'Re: {{subject}}',
                bodyTemplate: '',
                fallbackQuestions: [],
              },
            }),
            ...(loaded?.emailSystem?.intake || {}),
            alerts: {
              ...(
                INITIAL_SETTINGS.emailSystem?.intake?.alerts || {
                  enabled: true,
                  businessHoursOnly: false,
                  firstResponseSlaMinutes: 15,
                  escalationSteps: [],
                  autoResolveOnFirstOutbound: true,
                }
              ),
              ...(loaded?.emailSystem?.intake?.alerts || {}),
            },
            automation: {
              ...(
                INITIAL_SETTINGS.emailSystem?.intake?.automation || {
                  enabled: true,
                  replyOnFirstInboundOnly: true,
                  applyToCategories: ['CHARTER', 'SCHEDULED', 'UNCERTAIN'],
                  mailboxKeys: [],
                  cooldownMinutes: 60,
                  askMissingFields: true,
                  subjectTemplate: 'Re: {{subject}}',
                  bodyTemplate: '',
                  fallbackQuestions: [],
                }
              ),
              ...(loaded?.emailSystem?.intake?.automation || {}),
            },
          },
        },
        agent: loaded?.agent
          ? {
              ...INITIAL_SETTINGS.agent,
              ...loaded.agent,
              provider: { ...INITIAL_SETTINGS.agent.provider, ...loaded.agent.provider },
              providerConnections: Array.isArray(loaded.agent.providerConnections) && loaded.agent.providerConnections.length > 0
                ? loaded.agent.providerConnections.map((connection: any, index: number) => ({
                    ...INITIAL_SETTINGS.agent.providerConnections?.[0],
                    id: connection?.id || `connection-${index + 1}`,
                    label: connection?.label || `Provider ${index + 1}`,
                    ...connection,
                  }))
                : INITIAL_SETTINGS.agent.providerConnections,
              taskRouting: {
                ...INITIAL_SETTINGS.agent.taskRouting,
                ...(loaded.agent.taskRouting || {}),
              },
              modules: { ...INITIAL_SETTINGS.agent.modules, ...loaded.agent.modules },
              rolesByModule: loaded.agent.rolesByModule || INITIAL_SETTINGS.agent.rolesByModule,
              enforcement: { ...INITIAL_SETTINGS.agent.enforcement, ...(loaded.agent.enforcement || {}) },
              enforcementByModule: loaded.agent.enforcementByModule || INITIAL_SETTINGS.agent.enforcementByModule,
              ruleToggles: loaded.agent.ruleToggles || INITIAL_SETTINGS.agent.ruleToggles,
              customRulesByModule: loaded.agent.customRulesByModule || INITIAL_SETTINGS.agent.customRulesByModule,
              knowledgeBaseNotesByModule: loaded.agent.knowledgeBaseNotesByModule || INITIAL_SETTINGS.agent.knowledgeBaseNotesByModule,
              guidance: { ...INITIAL_SETTINGS.agent.guidance, ...loaded.agent.guidance },
              communications: {
                whatsapp: {
                  ...INITIAL_SETTINGS.agent.communications.whatsapp,
                  ...(loaded.agent.communications?.whatsapp || {}),
                  draftGuardian: {
                    ...INITIAL_SETTINGS.agent.communications.whatsapp.draftGuardian,
                    ...(loaded.agent.communications?.whatsapp?.draftGuardian || {}),
                  },
                  contextAssistant: {
                    ...INITIAL_SETTINGS.agent.communications.whatsapp.contextAssistant,
                    ...(loaded.agent.communications?.whatsapp?.contextAssistant || {}),
                  },
                  intelligence: {
                    ...INITIAL_SETTINGS.agent.communications.whatsapp.intelligence,
                    ...(loaded.agent.communications?.whatsapp?.intelligence || {}),
                  },
                  replyWatcher: {
                    ...INITIAL_SETTINGS.agent.communications.whatsapp.replyWatcher,
                    ...(loaded.agent.communications?.whatsapp?.replyWatcher || {}),
                  },
                },
                email: {
                  ...INITIAL_SETTINGS.agent.communications.email,
                  ...(loaded.agent.communications?.email || {}),
                  draftGuardian: {
                    ...INITIAL_SETTINGS.agent.communications.email.draftGuardian,
                    ...(loaded.agent.communications?.email?.draftGuardian || {}),
                  },
                  contextAssistant: {
                    ...INITIAL_SETTINGS.agent.communications.email.contextAssistant,
                    ...(loaded.agent.communications?.email?.contextAssistant || {}),
                  },
                  intelligence: {
                    ...INITIAL_SETTINGS.agent.communications.email.intelligence,
                    ...(loaded.agent.communications?.email?.intelligence || {}),
                  },
                  replyWatcher: {
                    ...INITIAL_SETTINGS.agent.communications.email.replyWatcher,
                    ...(loaded.agent.communications?.email?.replyWatcher || {}),
                  },
                },
              },
            }
          : INITIAL_SETTINGS.agent,
        loginPage: {
          banners: Array.isArray(loaded?.loginPage?.banners) ? loaded.loginPage.banners : (INITIAL_SETTINGS.loginPage?.banners ?? []),
          footer: loaded?.loginPage?.footer ?? INITIAL_SETTINGS.loginPage?.footer,
        },
        scheduledSop: {
          ...(INITIAL_SETTINGS.scheduledSop || {
            enabled: true,
            operatorCanDecline: false,
            requireCommandApprovalForDecline: true,
            declineRoles: ['DFO', 'ADMIN', 'CEO'],
            commandChainRoles: ['RESERVATIONS', 'DFO', 'ADMIN'],
            targetResponseMinutes: 15,
            initialResponseTemplate: '',
            pendingApprovalTemplate: '',
          }),
          ...(loaded?.scheduledSop || {}),
        },
      };
    } catch (error: any) {
      console.error('[AdminService] Failed to fetch settings:', error?.message || error);
      // Return default settings on error
      const { INITIAL_SETTINGS } = await import('../constants');
      return INITIAL_SETTINGS;
    }
  },

  getSelfPermissions: async (): Promise<{ role: string; permissions: Record<string, any> }> => {
    const response = await api.get('/admin/settings/permissions-self');
    return response.data?.data || { role: 'RESERVATIONS', permissions: {} };
  },

  getRuntimeSelfSettings: async (): Promise<{
    role: string;
    permissions: Record<string, any>;
    settings: AdminSettings;
  }> => {
    const response = await api.get('/admin/settings/runtime-self');
    const payload = response.data?.data || {};
    const loaded = payload?.settings || {};
    const { INITIAL_SETTINGS } = await import('../constants');
    const merged: AdminSettings = {
      ...INITIAL_SETTINGS,
      ...loaded,
      sleepOverPolicy: {
        ...INITIAL_SETTINGS.sleepOverPolicy,
        ...(loaded?.sleepOverPolicy || {}),
      },
      routeDefaults: {
        ...INITIAL_SETTINGS.routeDefaults,
        ...(loaded?.routeDefaults || {}),
      },
      cancellationPolicies: {
        ...INITIAL_SETTINGS.cancellationPolicies,
        ...(loaded?.cancellationPolicies || {}),
        charter: {
          ...INITIAL_SETTINGS.cancellationPolicies.charter,
          ...(loaded?.cancellationPolicies?.charter || {}),
        },
        scheduled: {
          ...INITIAL_SETTINGS.cancellationPolicies.scheduled,
          ...(loaded?.cancellationPolicies?.scheduled || {}),
        },
      },
      discountCapsByRole: loaded?.discountCapsByRole || INITIAL_SETTINGS.discountCapsByRole,
      permissions: loaded?.permissions || INITIAL_SETTINGS.permissions,
      whatsappTransfer: {
        ...(INITIAL_SETTINGS.whatsappTransfer || {
          enabled: true,
          popupEnabled: true,
          autoIntroEnabled: true,
          allowOperatorOverride: true,
          autoIntroTemplate: "Hi, I'm taking over your request now. I will assist you from here.",
          returnToOwnerTemplate: "Welcome back {{clientName}}, I am now back on your chat and will continue assisting you.",
          askFeedbackBeforeReturn: false,
          feedbackPromptTemplate: "Before I return you to my colleague, was this support helpful? (Yes/No)",
        }),
        ...(loaded?.whatsappTransfer || {}),
      },
      whatsappRecall: {
        ...(INITIAL_SETTINGS.whatsappRecall || {
          enabled: true,
          maxRecallMinutes: 120,
          allowLeadershipOverride: true,
          requireReason: false,
          allowMediaRecall: true,
        }),
        ...(loaded?.whatsappRecall || {}),
      },
      sPhone: {
        ...(INITIAL_SETTINGS.sPhone || {
          enabled: false,
          wsServer: '',
          domain: '',
          autoRegister: true,
          defaultOutboundPrefix: '',
          iceServers: ['stun:stun.l.google.com:19302'],
          accounts: [],
        }),
        ...(loaded?.sPhone || {}),
        accounts: Array.isArray(loaded?.sPhone?.accounts) ? loaded.sPhone.accounts : (INITIAL_SETTINGS.sPhone?.accounts || []),
      },
    };
    return {
      role: payload?.role || 'RESERVATIONS',
      permissions: payload?.permissions || {},
      settings: merged,
    };
  },

  /**
   * Save system settings to backend
   */
  saveSettings: async (settings: AdminSettings): Promise<AdminSettings> => {
    try {
      const response = await api.put('/admin/settings', { settings });
      return response.data?.data || response.data || settings;
    } catch (error: any) {
      console.error('[AdminService] Failed to save settings:', error?.message || error);
      throw error;
    }
  },

  previewEmailReplyTemplate: async (template: EmailReplyTemplate, sampleContext?: Record<string, any>): Promise<{
    usedScenario: string;
    usedVariables: string[];
    missingVariables: string[];
    warnings: string[];
    renderedSubject: string;
    renderedBody: string;
  }> => {
    const response = await api.post('/admin/agent/email/reply-templates/preview', {
      template,
      sampleContext: sampleContext || {},
    });
    return response.data?.data || {
      usedScenario: template.scenario,
      usedVariables: [],
      missingVariables: [],
      warnings: [],
      renderedSubject: '',
      renderedBody: '',
    };
  },

  seedEmailReplyTemplates: async (
    mode: 'missing_only' | 'replace_existing',
  ): Promise<{
    mode: 'missing_only' | 'replace_existing';
    createdCount: number;
    updatedCount: number;
    scenariosAffected: string[];
    templates: EmailReplyTemplate[];
  }> => {
    const response = await api.post('/admin/agent/email/reply-templates/seed-defaults', { mode });
    const data = response?.data?.data || {};
    return {
      mode: (data.mode || mode) as 'missing_only' | 'replace_existing',
      createdCount: Number(data.createdCount || 0),
      updatedCount: Number(data.updatedCount || 0),
      scenariosAffected: Array.isArray(data.scenariosAffected)
        ? data.scenariosAffected.map((value: any) => String(value || '').trim()).filter(Boolean)
        : [],
      templates: Array.isArray(data.templates) ? data.templates : [],
    };
  },

  listEmailGoldExamples: async (params?: {
    scenario?: string;
    active?: boolean;
    search?: string;
    limit?: number;
    module?: 'EMAIL';
  }): Promise<GoldReplyExample[]> => {
    const response = await api.get('/admin/agent/email/gold-examples', {
      params: {
        module: params?.module || 'EMAIL',
        scenario: params?.scenario || undefined,
        active: typeof params?.active === 'boolean' ? String(params.active) : undefined,
        search: params?.search || undefined,
        limit: params?.limit || undefined,
      },
    });
    return Array.isArray(response.data?.data) ? response.data.data : [];
  },

  createEmailGoldExample: async (payload: Omit<GoldReplyExample, 'id' | 'createdAt' | 'updatedAt'>): Promise<GoldReplyExample> => {
    const response = await api.post('/admin/agent/email/gold-examples', payload);
    return response.data?.data;
  },

  updateEmailGoldExample: async (id: string, payload: Omit<GoldReplyExample, 'id' | 'createdAt' | 'updatedAt'>): Promise<GoldReplyExample> => {
    const response = await api.put(`/admin/agent/email/gold-examples/${encodeURIComponent(id)}`, payload);
    return response.data?.data;
  },

  toggleEmailGoldExample: async (id: string, active?: boolean): Promise<GoldReplyExample> => {
    const response = await api.patch(`/admin/agent/email/gold-examples/${encodeURIComponent(id)}/toggle`, { active });
    return response.data?.data;
  },

  deleteEmailGoldExample: async (id: string): Promise<GoldReplyExample> => {
    const response = await api.delete(`/admin/agent/email/gold-examples/${encodeURIComponent(id)}`);
    return response.data?.data;
  },

  promoteEmailGoldExample: async (payload: {
    sourceSuggestionLogId?: string;
    module?: 'EMAIL';
    scenario?: string;
    title: string;
    inputSummary?: string;
    approvedSubject?: string;
    approvedBody?: string;
    tone?: string;
    tags?: string[];
    approvalNote?: string;
    active?: boolean;
  }): Promise<GoldReplyExample> => {
    const response = await api.post('/admin/agent/email/gold-examples/promote', payload);
    return response.data?.data;
  },

  /**
   * Fetch all named guided tour definitions configured by an admin.
   * Returns an empty array if none have been saved yet (components use built-in defaults).
   */
  getAllTours: async (): Promise<TourDefinition[]> => {
    try {
      const response = await api.get('/admin/tour-steps');
      return response.data?.tours ?? [];
    } catch {
      return [];
    }
  },
  testEmailAccount: async (payload: { accountId: string; to: string; subject?: string; body?: string }) => {
    try {
      const response = await api.post('/admin/settings/outbound/test-email', payload);
      return response.data?.data || response.data;
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to send test email';
      console.error('[AdminService] Failed to send test email:', message);
      throw new Error(message);
    }
  },
  getAgentHistory: async (): Promise<any[]> => {
    try {
      const response = await api.get('/admin/settings/agent-history');
      return response.data?.data || [];
    } catch (error: any) {
      console.error('[AdminService] Failed to fetch agent history:', error?.message || error);
      return [];
    }
  },
  rollbackAgent: async (snapshotId: string): Promise<AdminSettings | null> => {
    try {
      const response = await api.post('/admin/settings/agent-rollback', { snapshotId });
      return response.data?.data || response.data || null;
    } catch (error: any) {
      console.error('[AdminService] Failed to rollback agent settings:', error?.message || error);
      return null;
    }
  },
  getAuditLogs: async (params?: {
    limit?: number;
    from?: string;
    to?: string;
    action?: string;
    entity?: string;
    userId?: string;
    search?: string;
    cursor?: string;
  }): Promise<{ data: SystemAuditLogEntry[]; nextCursor: string | null; error?: string }> => {
    try {
      const response = await api.get('/admin/audit/logs', { params });
      return {
        data: response.data?.data || [],
        nextCursor: response.data?.nextCursor || null,
      };
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to fetch audit logs';
      console.error('[AdminService] Failed to fetch audit logs:', message);
      return { data: [], nextCursor: null, error: message };
    }
  },
  exportAuditCsv: async (params?: {
    limit?: number;
    from?: string;
    to?: string;
    action?: string;
    entity?: string;
    userId?: string;
    search?: string;
  }): Promise<Blob | null> => {
    try {
      const response = await api.get('/admin/audit/export', {
        params,
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (error: any) {
      console.error('[AdminService] Failed to export audit CSV:', error?.message || error);
      return null;
    }
  },
  getAuditArchives: async (): Promise<Array<{ name: string; size: number; createdAt: string }>> => {
    try {
      const response = await api.get('/admin/audit/archives');
      return response.data?.data || [];
    } catch (error: any) {
      console.error('[AdminService] Failed to fetch audit archives:', error?.message || error);
      return [];
    }
  },
  downloadAuditArchive: async (name: string): Promise<Blob | null> => {
    try {
      const response = await api.get(`/admin/audit/archives/${encodeURIComponent(name)}`, {
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (error: any) {
      console.error('[AdminService] Failed to download audit archive:', error?.message || error);
      return null;
    }
  },
  verifyAuditChain: async (limit?: number): Promise<{ ok: boolean; checked: number; issues: any[] } | null> => {
    try {
      const response = await api.get('/admin/audit/verify', { params: { limit } });
      return response.data?.data || null;
    } catch (error: any) {
      console.error('[AdminService] Failed to verify audit chain:', error?.message || error);
      return null;
    }
  },
  getOperationsTvPublicAccess: async (): Promise<{
    hasToken: boolean;
    active: boolean;
    alias?: string | null;
    createdAt?: string | null;
    expiresAt?: string | null;
    revokedAt?: string | null;
    lastGeneratedBy?: string | null;
  } | null> => {
    try {
      const response = await api.get('/admin/settings/operations-tv-public-access');
      return response.data?.data || null;
    } catch (error: any) {
      console.error('[AdminService] Failed to load Operations TV public access settings:', error?.message || error);
      return null;
    }
  },
  generateOperationsTvPublicToken: async (
    expiryDays: number = 30,
    alias?: string | null
  ): Promise<{ token: string; alias?: string | null; expiresAt: string } | null> => {
    try {
      const response = await api.post('/admin/settings/operations-tv-public-access/generate', { expiryDays, alias });
      return response.data?.data || null;
    } catch (error: any) {
      console.error('[AdminService] Failed to generate Operations TV public token:', error?.message || error);
      return null;
    }
  },
  revokeOperationsTvPublicToken: async (): Promise<boolean> => {
    try {
      const response = await api.post('/admin/settings/operations-tv-public-access/revoke');
      return !!response.data?.success;
    } catch (error: any) {
      console.error('[AdminService] Failed to revoke Operations TV public token:', error?.message || error);
      return false;
    }
  },
  getCommsTvTokenStatus: async (): Promise<{
    hasToken: boolean;
    active: boolean;
    alias?: string | null;
    createdAt?: string | null;
    expiresAt?: string | null;
    revokedAt?: string | null;
    lastGeneratedBy?: string | null;
  } | null> => {
    try {
      const response = await api.get('/admin/comms-tv-token/status');
      return response.data?.data || null;
    } catch (error: any) {
      console.error('[AdminService] Failed to load Comms TV token status:', error?.message || error);
      return null;
    }
  },
  generateCommsTvToken: async (
    expiryDays: number = 30,
    alias?: string | null
  ): Promise<{ token: string; alias?: string | null; expiresAt: string } | null> => {
    try {
      const response = await api.post('/admin/comms-tv-token/generate', { expiryDays, alias });
      return response.data?.data || null;
    } catch (error: any) {
      console.error('[AdminService] Failed to generate Comms TV token:', error?.message || error);
      return null;
    }
  },
  revokeCommsTvToken: async (): Promise<boolean> => {
    try {
      const response = await api.post('/admin/comms-tv-token/revoke');
      return !!response.data?.success;
    } catch (error: any) {
      console.error('[AdminService] Failed to revoke Comms TV token:', error?.message || error);
      return false;
    }
  },

  getEmailPolishAnalytics: async (days = 30): Promise<{
    days: number;
    totals: {
      totalPolishUses: number;
      totalSentReplies: number;
      polishPercentOfSentReplies: number;
      sentAfterPolishRate: number;
      editedAfterPolishRate: number;
    };
    byMode: Array<{ mode: string; count: number }>;
    byOperator: Array<{ userId: string; name: string; email?: string | null; count: number; sentAfterPolish: number; editedAfterPolish: number }>;
  } | null> => {
    try {
      const response = await api.get('/admin/agent/email/polish-analytics', { params: { days } });
      return response.data?.data || null;
    } catch (error: any) {
      console.error('[AdminService] Failed to load email polish analytics:', error?.message || error);
      return null;
    }
  },
};
